import { CorporateAdmins } from '@platform/client';
import type { AcceptedApplicantData, CohortData } from '@platform/client/schemas';
import { Icon, Input, PillBadge } from '@platform/components';
import { useQueryClient } from '@tanstack/react-query';
import cx from 'classix';
import { useEffect, useMemo, useState } from 'react';
import { formatDateStr } from '../../../dashboard/my-projects/utils';
import type { AcceptedGroup } from '../types';
import { NameCell } from '../NameCell';
import { ProjectLink } from './ProjectLink';
import {
  capitalize,
  groupAcceptedByMatch,
  sortAcceptedGroups,
  type AcceptedSortColumn,
  type SortDirection,
} from './utils';

const RECENT_UPDATE_MAX_LENGTH = 200;
const RECENT_UPDATE_SAVE_DEBOUNCE_MS = 800;

const SortableHeader = ({
  label,
  column,
  activeColumn,
  direction,
  onSort,
  className,
}: {
  label: string;
  column: AcceptedSortColumn;
  activeColumn: AcceptedSortColumn | null;
  direction: SortDirection;
  onSort: (column: AcceptedSortColumn) => void;
  className: string;
}) => {
  const isActive = activeColumn === column;
  return (
    <th className={className}>
      <div
        className='flex items-center gap-1 cursor-pointer select-none'
        onClick={() => onSort(column)}
      >
        {label}
        {isActive && direction === 'asc' ?
          <Icon type='chevron-up' size='12' />
        : isActive && direction === 'desc' ?
          <Icon type='chevron-down' size='12' />
        : <span className='inline-flex flex-col items-center justify-center' aria-hidden='true'>
            <Icon type='chevron-up' size='12' />
            <Icon type='chevron-down' size='12' additionalClassNames='-mt-1' />
          </span>
        }
      </div>
    </th>
  );
};

const RecentUpdateCell = ({
  cohortId,
  row,
  isPlatformAdmin,
}: {
  cohortId: number;
  row: AcceptedApplicantData;
  isPlatformAdmin: boolean;
}) => {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<string | null>(null);
  const { mutate: updateRecentUpdate } = CorporateAdmins.useUpdateRecentUpdate({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: CorporateAdmins.getGetApplicantsQueryKey(cohortId),
        });
      },
    },
  });

  const savedText = row.recentUpdate?.text ?? '';
  useEffect(() => {
    if (draft === null || draft === savedText) return;
    const timeout = setTimeout(() => {
      updateRecentUpdate({ cohortId, userId: row.id, data: { notes: draft || null } });
    }, RECENT_UPDATE_SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [draft, savedText, cohortId, row.id, updateRecentUpdate]);

  if (!isPlatformAdmin) {
    if (!row.recentUpdate) {
      return <span className='text-sm text-gray-400'>No updates yet</span>;
    }
    return (
      <div>
        <p className='text-sm text-gray-700'>{row.recentUpdate.text}</p>
        <p className='text-xs text-gray-400'>{formatDateStr(row.recentUpdate.updatedAt)}</p>
      </div>
    );
  }

  const value = draft ?? savedText;
  return (
    <Input
      type='multiline'
      value={value}
      onChange={(e) => setDraft(e.target.value)}
      characterCount={RECENT_UPDATE_MAX_LENGTH}
      placeholder='Add an update'
    />
  );
};

export function AcceptedStatusTable({
  data,
  cohort,
  cohortId,
  isPlatformAdmin,
}: {
  data: AcceptedApplicantData[];
  cohort: CohortData;
  cohortId: number;
  isPlatformAdmin: boolean;
}) {
  const isTeamsCohort = cohort.type === 'Teams';
  // Team and Individual-Assigned cohorts are pre-matched by the program team before kickoff -
  // there's no self-service "Discovery" step for the professional, so an unmatched participant
  // here is "Not Matched" rather than "still in Discovery".
  const isAssignedCohort = isTeamsCohort || cohort.type === 'Individual - Assigned';
  const groups = useMemo<AcceptedGroup[]>(
    () =>
      isTeamsCohort ?
        groupAcceptedByMatch(data)
      : data.map((row, idx) => ({
          key: String(idx),
          rows: [row],
          match: row.matchData,
          matchProgress: row.matchProgress,
          isOffTrack: row.isOffTrack,
        })),
    [data, isTeamsCohort],
  );

  const [sortCol, setSortCol] = useState<AcceptedSortColumn | null>(null);
  const [sortDir, setSortDir] = useState<SortDirection>(null);

  const handleSort = (column: AcceptedSortColumn) => {
    if (sortCol !== column) {
      setSortCol(column);
      setSortDir('asc');
      return;
    }
    if (sortDir === 'asc') {
      setSortDir('desc');
      return;
    }
    setSortCol(null);
    setSortDir(null);
  };

  const sortedGroups = useMemo(
    () => sortAcceptedGroups(groups, sortCol, sortDir),
    [groups, sortCol, sortDir],
  );
  const headerCellClass =
    'border-l border-gray-100 px-4 align-middle text-md font-semibold text-gray-500';

  return (
    <div className='mt-4 overflow-hidden rounded-xl border border-gray-100 bg-white'>
      {data.length === 0 ?
        <div className='px-4 py-8 text-center text-sm text-gray-400'>
          No applicants in this group
        </div>
      : <table className='w-full border-collapse text-left text-sm'>
          <thead className='sticky top-0 z-10 bg-gray-50'>
            <tr className='h-[75px]'>
              <SortableHeader
                label='Participant Name'
                column='name'
                activeColumn={sortCol}
                direction={sortDir}
                onSort={handleSort}
                className='pl-4 align-middle text-md font-semibold text-gray-500'
              />
              <SortableHeader
                label='Matched To'
                column='project'
                activeColumn={sortCol}
                direction={sortDir}
                onSort={handleSort}
                className={headerCellClass}
              />
              <SortableHeader
                label='Start Date'
                column='startDate'
                activeColumn={sortCol}
                direction={sortDir}
                onSort={handleSort}
                className={headerCellClass}
              />
              <SortableHeader
                label='End Date'
                column='endDate'
                activeColumn={sortCol}
                direction={sortDir}
                onSort={handleSort}
                className={headerCellClass}
              />
              <SortableHeader
                label='Cohort Milestone'
                column='status'
                activeColumn={sortCol}
                direction={sortDir}
                onSort={handleSort}
                className={headerCellClass}
              />
              <th className={headerCellClass}>Recent updates</th>
            </tr>
          </thead>
          <tbody>
            {sortedGroups.map((group, gIdx) => {
              const tint = isTeamsCohort && gIdx % 2 === 1 ? 'bg-secondary-brand-25' : '';
              return group.rows.map((row, idx) => {
                const isFirst = idx === 0;
                return (
                  <tr key={row.id} className={cx(tint, isFirst && 'border-t border-gray-100')}>
                    <td
                      className={cx(
                        'py-3 pl-4 align-middle',
                        !isFirst && 'border-t border-gray-100',
                      )}
                    >
                      <NameCell name={row.name} email={row.email} />
                    </td>
                    {isFirst && (
                      <>
                        <td
                          rowSpan={group.rows.length}
                          className='border-l border-gray-100 px-4 align-middle'
                        >
                          {group.match ?
                            <ProjectLink
                              name={group.match.projectName}
                              projectId={group.match.projectId}
                            />
                          : null}
                        </td>
                        <td
                          rowSpan={group.rows.length}
                          className='border-l border-gray-100 px-4 align-middle'
                        >
                          {group.match ? formatDateStr(group.match.projectStartDate) : null}
                        </td>
                        <td
                          rowSpan={group.rows.length}
                          className='border-l border-gray-100 px-4 align-middle'
                        >
                          {group.match ? formatDateStr(group.match.projectEndDate) : null}
                        </td>
                        <td
                          rowSpan={group.rows.length}
                          className='border-l border-gray-100 px-4 align-middle'
                        >
                          <div className='flex flex-col items-start gap-1'>
                            <span>
                              {group.matchProgress ?
                                capitalize(group.matchProgress)
                              : isAssignedCohort ?
                                'Not Matched'
                              : 'Discovery'}
                            </span>
                            {group.isOffTrack && (
                              <PillBadge variant='plain' color='warning' size='sm'>
                                Off track
                              </PillBadge>
                            )}
                          </div>
                        </td>
                      </>
                    )}
                    <td
                      className={cx(
                        'border-l border-gray-100 px-4 align-middle',
                        !isFirst && 'border-t border-gray-100',
                      )}
                    >
                      <RecentUpdateCell
                        cohortId={cohortId}
                        row={row}
                        isPlatformAdmin={isPlatformAdmin}
                      />
                    </td>
                  </tr>
                );
              });
            })}
          </tbody>
        </table>
      }
    </div>
  );
}
